import { message } from "antd";
import { isEntityId, loadFailureKind, replacePathSegment } from "common/businessAreaRoute";
import { showError, showInfo, showSuccess } from "common/ToastNotifications";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import { MessageBox } from "components/atoms/MessageBox";
import OkCancelConfirmationBox from "components/molecules/OkCancelConfirmationBox";
import BusinessAreaLoadError from "components/molecules/BusinessAreaLoadError";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import AutoTaggingModal from "components/organisms/AutoTaggingModal";
import ManageSchemaModel from "components/organisms/ManageSchemaModel";
import SchemaExport from "components/organisms/SchemaExport";
import SchemaModelCreateFromSystemConnection from "components/organisms/SchemaModelCreateFromSystemConnection";
import SchemaModelList from "components/organisms/SchemaModelList";
import SchemaSuggestionReview from "components/organisms/SchemaSuggestionReview";
import SystemConnectionSelector from "components/organisms/SystemConnectionSelector";
import AppPaths from "constants/appPaths";
import flatten from "flat";
import _ from "lodash";
import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";
import { selectSuggestionCounts } from "store/schemaProfile/reducer";
import { applyAcceptedSuggestions, collectAcceptedPicklistCandidates } from "common/applySchemaSuggestions";
import SchemaModelAudit from "./SchemaModelAudit";
import SchemaModelCreateAutomation from "./SchemaModelCreateAutomation";
import SchemaModelLineage from "./SchemaModelLineage";

class SchemaModels extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fetchingUserBusinessArea: true,
            fetchingSchemaModels: true,
            fetchingSchemaModel: true,
            fetchingPermission: true,
            schemaModelsDataFetched: false,
            busy: true,
            busyMessage: "Getting schema models...",
            // Why the business area could not be loaded: "notFound", "denied" or "failed".
            businessAreaFailure: null,
            createSchemaManually: false,
            createFromSystemConnection: false,
            manageParsedSchemaModelCancelled: true,
            // PRE-2722: holds the pending schema name while suggestions are
            // under review. Null means no review step is showing.
            reviewingSuggestionsFor: null,
            manageSchemaModelErrors: {},
            selectedSystemConnection: null,
            fetchingSystemConnections: false,
            selectedTable: null,
            showAutoTagModal: false,
            autoTagSuggestions: null,
            submittedSchemaFieldNamesByIndex: {},
        };
        this.manageSchemaModelRef = React.createRef();
    }

    normalizeFormErrors = (errors) => {
        if (!errors) {
            return {};
        }

        if (Array.isArray(errors)) {
            return errors.reduce((acc, item) => {
                if (item && item.name) {
                    acc[item.name] = item.errors;
                }
                return acc;
            }, {});
        }

        return errors;
    };

    getSchemaFieldNamesByIndex = (fields) => {
        return (fields || []).reduce((acc, field, index) => {
            acc[index] = field?.name || field?.description || `Field ${index + 1}`;
            return acc;
        }, {});
    };

    getFormErrorSummary = (errors, fieldNamesByIndex = {}) => {
        if (_.isEmpty(errors)) {
            return "";
        }

        const messages = [];
        const collectMessages = (value, path = "") => {
            if (!value) {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => collectMessages(item, path));
            } else if (_.isPlainObject(value)) {
                Object.keys(value).forEach((key) => collectMessages(value[key], path ? `${path}.${key}` : key));
            } else {
                const fieldIndex = path.match(/^fields\.(\d+)(?:\.|$)/)?.[1];
                const fieldName = fieldIndex !== undefined ? fieldNamesByIndex[fieldIndex] : null;
                const errorMessages = `${value}`.split("\n").filter(Boolean);

                errorMessages.forEach((message) => {
                    messages.push(fieldName ? `${fieldName}: ${message}` : message);
                });
            }
        };

        collectMessages(errors);
        return messages.join("; ");
    };

    getSchemaModelFailureMessage = (result, normalizedFormErrors, fallbackMessage) => {
        const formErrorSummary = this.getFormErrorSummary(normalizedFormErrors, this.state.submittedSchemaFieldNamesByIndex);
        const resultMessage = result.message || "";
        const isGenericValidationMessage = resultMessage.trim().toLowerCase() === "request body validation failed.";

        return (
            (isGenericValidationMessage && formErrorSummary) ||
            resultMessage ||
            formErrorSummary ||
            (!_.isEmpty(normalizedFormErrors) && "Please fix the errors in the form") ||
            fallbackMessage
        );
    };

    componentDidMount() {
        this.props.onManageSchemaModelRefReady && this.props.onManageSchemaModelRefReady(this.manageSchemaModelRef);
        if (!this.props.checkedKeys) {
            if (!this.props.keyExists) {
                this.getKeyByTypeExists();
            }
        }
        this.showBreadCrumbNavigationItems();
        switch (this.props.action && this.props.action.toLowerCase()) {
            case "create":
            case "history":
            case "export":
            case "lineage":
                this.setState({
                    busyMessage: "Please wait...",
                });
                break;
            case "view":
            case "edit":
                this.setState({
                    busyMessage: "Getting schema model...",
                });
                break;
            default:
                this.setState({
                    busyMessage: "Getting schema models...",
                });
        }
        this.props.getUserSchemasPermission();
        this.props.getUserBusinessAreaByName(this.props.businessArea);
        this.setSchemaPermissions(this.props.permission, this.props.userBusinessAreas[this.props.businessArea]);
        if (this.props.userBusinessAreas[this.props.businessArea]) {
            this.fetchSchemaModelsData();
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.onGetUserSchemaModelsPermissionResultChanged(prevProps);
        this.onGetUserBusinessAreaByNameResultChanged(prevProps);
        this.onGetSchemaModelsResultChanged(prevProps);
        this.onGetSchemaModelResultChanged(prevProps);
        this.onCreateSchemaModelResultChanged(prevProps);
        this.onDeleteSchemaModelResultChanged(prevProps);
        this.onUpdateSchemaModelResultChanged(prevProps);
        this.onGetKeyByTypeExistsResultUpdate(prevProps);
        this.onAutoTaggingResultChanged(prevProps);
        this.onBulkCreateDataTagResultChanged(prevProps);
    }

    componentWillUnmount() {
        this.props.onSchemaModelListUnLoaded && this.props.onSchemaModelListUnLoaded();
        this.props.onManageSchemaModelRefReady && this.props.onManageSchemaModelRefReady(null);
    }

    onGetUserSchemaModelsPermissionResultChanged(prevProps) {
        if (this.props.getUserSchemaModelsPermissionResult && this.props.getUserSchemaModelsPermissionResult !== prevProps.getUserSchemaModelsPermissionResult) {
            if (!this.props.getUserSchemaModelsPermissionResult.success) {
                this.setState({
                    permissionDenied: true,
                    fetchingPermission: false,
                });
            } else {
                this.setState({
                    fetchingPermission: false,
                });
                this.setSchemaPermissions(this.props.permission, this.props.userBusinessAreas[this.props.businessArea]);
            }
        }
    }

    onAutoTaggingResultChanged(prevProps) {
        if (this.props.autoTaggingResult && this.props.autoTaggingResult !== prevProps.autoTaggingResult) {
            if (this.props.autoTaggingResult.loading) {
                return;
            }
            if (this.props.autoTaggingResult.success === false) {
                showError(this.props.autoTaggingResult.message || "Unable to retrieve auto tagging suggestions at this moment.");
                this.setState({
                    showAutoTagModal: false,
                    autoTagSuggestions: null,
                });
            } else if (this.props.autoTaggingResult.success === true) {
                if (this.props.autoTaggingData && this.props.autoTaggingData.status === "TIMEOUT") {
                    showInfo("Auto tagging generation is taking longer than expected. Please check again in a moment.");
                    this.setState({
                        showAutoTagModal: false,
                        autoTagSuggestions: null,
                    });
                } else {
                    this.setState({
                        showAutoTagModal: true,
                        autoTagSuggestions: this.props.autoTaggingData?.data || this.props.autoTaggingData,
                    });
                    showSuccess("Auto tagging suggestions generated successfully.");
                }
            }
        }
    }

    onBulkCreateDataTagResultChanged(prevProps) {
        if (this.props.bulkCreateDataTagResult && this.props.bulkCreateDataTagResult !== prevProps.bulkCreateDataTagResult) {
            if (!this.props.bulkCreateDataTagResult.success) {
                showError(this.props.bulkCreateDataTagResult.message || "Unable to create data tags in bulk at this moment.");
            } else {
                showSuccess("Data tags created successfully ");
                // Add the newly created tags to business area
                if (this.props.bulkCreateTagsResult && this.props.bulkCreateTagsResult.length > 0) {
                    this.props.addBulkCreatedTagsToBusinessArea(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, this.props.bulkCreateTagsResult);
                    message.success("Data tags added to business area successfully.");
                }
            }
        }
    }

    onGetKeyByTypeExistsResultUpdate = (prevProps) => {
        if (this.props.getKeyByTypeExistsResult && this.props.getKeyByTypeExistsResult !== prevProps.getKeyByTypeExistsResult) {
            if (this.props.checkedKeys && !this.props.keyExists) {
                // Only log, don't show error
                console.warn("OPEN_AI_KEY is not configured. Please add it in the system keys.");
            }
        }
    };

    onGetUserBusinessAreaByNameResultChanged(prevProps) {
        if (this.props.getUserBusinessAreaByNameResult && this.props.getUserBusinessAreaByNameResult !== prevProps.getUserBusinessAreaByNameResult) {
            if (!this.props.getUserBusinessAreaByNameResult.success) {
                this.setState({
                    businessAreaFailure: loadFailureKind(this.props.getUserBusinessAreaByNameResult),
                    busy: false,
                    fetchingUserBusinessArea: false,
                    fetchingSchemaModels: false,
                });
            } else {
                this.setState({
                    fetchingUserBusinessArea: false,
                });
                // Asked for by its id (a link built from ids): the page moves to its name.
                const { requestedName } = this.props.getUserBusinessAreaByNameResult;
                const resolved = Object.values(this.props.userBusinessAreas || {}).find((item) => item);
                if (requestedName === this.props.businessArea && isEntityId(requestedName) && resolved) {
                    const pathname = replacePathSegment(this.props.location.pathname, "businessareas", requestedName, resolved.name.toLowerCase());
                    this.props.history.replace({ ...this.props.location, pathname });
                    return;
                }
                this.setSchemaPermissions(this.props.permission, this.props.userBusinessAreas[this.props.businessArea]);
                if (this.props.userBusinessAreas[this.props.businessArea]) {
                    this.fetchSchemaModelsData();
                } else {
                    this.setState({
                        businessAreaFailure: "notFound",
                        fetchingSchemaModels: false,
                        busy: false,
                    });
                }
            }
        }
    }

    onGetSchemaModelsResultChanged(prevProps) {
        if (this.props.getSchemaModelsResult && this.props.getSchemaModelsResult !== prevProps.getSchemaModelsResult) {
            if (!this.props.getSchemaModelsResult.success) {
                if (this.props.getSchemaModelsResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to get schema models at this moment.");
                }
            } else {
                if (this.props.onSchemaModelListLoaded) {
                    this.props.onSchemaModelListLoaded();
                }
                let selectedSchemaModel = this.getSelectedSchemaModelFromHash();
                if (selectedSchemaModel) {
                    this.onSchemaModelSelected(selectedSchemaModel);
                }
            }
            this.setState({
                busy: false,
                fetchingSchemaModels: false,
            });
        }
    }

    onGetSchemaModelResultChanged(prevProps) {
        if (this.props.getSchemaModelResult && this.props.getSchemaModelResult !== prevProps.getSchemaModelResult) {
            if (!this.props.getSchemaModelResult.success) {
                if (this.props.getSchemaModelResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to get schema model.");
                }
            } else if (this.props.action === "history") {
                this.setState({
                    showHistory: true,
                });
            } else if (this.props.action === "export") {
                this.setState({
                    showExport: true,
                });
            } else if (this.props.action === "lineage") {
                this.setState({
                    showLineage: true,
                });
            }
            this.setState({
                busy: false,
                fetchingSchemaModel: false,
            });
            if (!this.props.schemaModelListResult || !this.props.schemaModelListResult.Items || this.props.schemaModelListResult.Items.length === 0) {
                this.showBreadCrumbNavigationItems();
            }
        }
    }

    onCreateSchemaModelResultChanged(prevProps) {
        if (this.props.createSchemaModelResult && this.props.createSchemaModelResult !== prevProps.createSchemaModelResult) {
            if (!this.props.createSchemaModelResult.success) {
                const normalizedFormErrors = this.normalizeFormErrors(this.props.createSchemaModelResult.fields);
                showError(this.getSchemaModelFailureMessage(this.props.createSchemaModelResult, normalizedFormErrors, "Schema model cannot be created."));
                this.setState({
                    busy: false,
                    manageSchemaModelErrors: normalizedFormErrors,
                    afterSaveCallback: null,
                });
            } else {
                this.setState({
                    busy: true,
                    busyMessage: "Schema model created. Please wait...",
                });
                if (this.state.afterSaveCallback) {
                    this.state.afterSaveCallback();
                    this.setState({
                        afterSaveCallback: null,
                    });
                } else {
                    setTimeout(() => {
                        this.setState({
                            busy: false,
                            busyMessage: "",
                        });
                    }, 2000);
                    showSuccess("Schema model created successfully.", () => {
                        if (this.state.goToEditSchema || this.state.parsedSchemaDefinition) {
                            this.showEditSchemaModel(this.props.createdSchemaModel.schemaId);
                        } else {
                            this.cancelManageSchemaModel();
                        }
                    });
                }
            }
        }
    }

    onDeleteSchemaModelResultChanged(prevProps) {
        if (this.props.deleteSchemaModelResult && this.props.deleteSchemaModelResult !== prevProps.deleteSchemaModelResult) {
            if (!this.props.deleteSchemaModelResult.success) {
                showError(this.props.deleteSchemaModelResult.message || "Schema model cannot be deleted.");
            } else {
                showSuccess("Schema model deleted successfully.");
            }
            this.setState({
                busy: false,
            });
        }
    }

    onUpdateSchemaModelResultChanged(prevProps) {
        if (this.props.updateSchemaModelResult && this.props.updateSchemaModelResult !== prevProps.updateSchemaModelResult) {
            if (!this.props.updateSchemaModelResult.success) {
                const normalizedFormErrors = this.normalizeFormErrors(this.props.updateSchemaModelResult.fields);
                const isPrimaryKeyChangeNotAllowed = this.props.updateSchemaModelResult.code === "PRIMARY_KEY_CHANGE_NOT_ALLOWED";

                showError(
                    (isPrimaryKeyChangeNotAllowed && this.props.updateSchemaModelResult.message) ||
                        this.getSchemaModelFailureMessage(this.props.updateSchemaModelResult, normalizedFormErrors, "Schema model cannot be updated."),
                );
                this.setState({
                    busy: false,
                    manageSchemaModelErrors: normalizedFormErrors,
                    afterSaveCallback: null,
                });
            } else if (this.props.action && this.props.updateSchemaModelResult.source !== "assistant") {
                // Editor flow: busy stays on through the post-save navigation/refresh.
                this.setState({
                    busy: true,
                    busyMessage: "Schema model updated. Please wait...",
                });
                if (this.state.afterSaveCallback) {
                    this.state.afterSaveCallback();
                    this.setState({
                        afterSaveCallback: null,
                    });
                } else {
                    showSuccess("Schema model updated successfully.", () => {
                        this.cancelManageSchemaModel();
                    });
                }
            } else {
                // Update came from outside the editor (e.g. AI assistant on the schema
                // page). Refresh the open schema/list in place and clear busy WITHOUT
                // navigating away — the user stays on the same page.
                const businessArea = this.props.userBusinessAreas && this.props.userBusinessAreas[this.props.businessArea];
                if (businessArea && businessArea.businessAreaId) {
                    this.props.getSchemaModels(businessArea.businessAreaId);
                    if (this.props.schemaId) {
                        this.props.getSchemaModel(businessArea.businessAreaId, this.props.schemaId);
                    }
                }
                this.setState({ busy: false, afterSaveCallback: null });
                showSuccess("Schema model updated successfully.");
            }
        }
    }

    showBreadCrumbNavigationItems = () => {
        if (this.props.setBreadcrumbNavigationItems) {
            let breadCrumbItems = [
                {
                    route: AppPaths.TENANT_HOME.replace(":tenant", this.props.tenant.toLowerCase()),
                    text: "Home",
                },
                {
                    route: AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(":businessarea", this.props.businessArea.toLowerCase()),
                    text: "Schemas",
                },
            ];
            if (this.props.action) {
                switch (this.props.action.toLowerCase()) {
                    case "create":
                        breadCrumbItems.push({
                            route: "",
                            text: "New Schema",
                        });
                        break;
                    case "view":
                    case "edit":
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: "",
                                    text: this.getSchemaModelName(),
                                },
                            ],
                        );
                        break;
                    case "history":
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                                        .replace(":businessarea", this.props.businessArea.toLowerCase())
                                        .replace(":schemaId", this.props.schemaId)
                                        .replace(":schemaAction", this.props.prevAction ?? "view"),
                                    text: this.getSchemaModelName(),
                                },
                                {
                                    route: "",
                                    text: "History",
                                },
                            ],
                        );
                        break;
                    case "export":
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: this.props.prevAction
                                        ? AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                                              .replace(":businessarea", this.props.businessArea.toLowerCase())
                                              .replace(":schemaId", this.props.schemaId)
                                              .replace(":schemaAction", this.props.prevAction)
                                        : "",
                                    text: this.getSchemaModelName(),
                                },
                                {
                                    route: "",
                                    text: "export",
                                },
                            ],
                        );
                        break;
                    case "lineage":
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: "",
                                    text: this.getSchemaModelName(),
                                },
                                {
                                    route: "",
                                    text: "Relationships",
                                },
                            ],
                        );
                        break;
                    default:
                        // Switch must include a default case
                        break;
                }
            }

            this.props.setBreadcrumbNavigationItems(breadCrumbItems);
        }
    };

    getSchemaModelName = () => {
        let schemaName = "";
        if (this.props.schemaModel && this.props.schemaModel.schemaId === this.props.schemaId) {
            schemaName = this.props.schemaModel.name;
        }
        if (!schemaName && this.props.schemaModelListResult && this.props.schemaModelListResult.Items && this.props.schemaModelListResult.Items.length > 0) {
            let schemaModel = this.props.schemaModelListResult.Items.find((schemModel) => schemModel.schemaId === this.props.schemaId);
            if (schemaModel) {
                schemaName = schemaModel.name;
            }
        }
        if (!schemaName && this.props.recentSchemaModelListResult && this.props.recentSchemaModelListResult.Items && this.props.recentSchemaModelListResult.Items.length > 0) {
            let schemaModel = this.props.recentSchemaModelListResult.Items.find((schemModel) => schemModel.schemaId === this.props.schemaId);
            if (schemaModel) {
                schemaName = schemaModel.name;
            }
        }
        if (!schemaName) {
            schemaName = this.props.schemaId;
        }
        return schemaName;
    };

    setSchemaPermissions = (schemaPermission, userBusinessArea) => {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        if (schemaPermission && userBusinessArea && userBusinessArea.user) {
            permission = {
                canAdd: schemaPermission.canAdd && userBusinessArea.user.canAdd,
                canView: schemaPermission.canView && userBusinessArea.user.canView,
                canEdit: schemaPermission.canEdit && userBusinessArea.user.canEdit,
                canDelete: schemaPermission.canDelete && userBusinessArea.user.canDelete,
            };
        }
        this.setState({
            permission,
        });
    };

    fetchSchemaModelsData = () => {
        if (this.state.schemaModelsDataFetched === false) {
            if (this.props.action) {
                this.props.getBusinessAreaDataForBusinessArea(this.props.userBusinessAreas[this.props.businessArea].businessAreaId);
            }

            if (!this.props.action) {
                this.props.getSchemaModels(this.props.userBusinessAreas[this.props.businessArea].businessAreaId);
            } else {
                this.props.getSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, this.props.schemaId);
                if (this.props.action === "view" || this.props.action === "edit") {
                    this.setState({
                        busy: true,
                        busyMessage: "Getting schema model...",
                    });
                } else if (this.props.action === "history") {
                    this.setState({
                        busy: true,
                        busyMessage: "Please wait...",
                        showHistory: false,
                    });
                } else if (this.props.action === "export") {
                    this.setState({
                        busy: true,
                        busyMessage: "Please wait...",
                        showExport: false,
                    });
                } else if (this.props.action === "lineage") {
                    this.setState({
                        busy: true,
                        busyMessage: "Please wait...",
                        showLineage: false,
                    });
                } else {
                    this.setState({
                        busy: false,
                    });
                }
            }
            this.setState({
                schemaModelsDataFetched: true,
            });
        }
    };

    onSchemaModelSelected = (schemaModel) => {
        this.props.onSchemaModelSelected({ schemaModel, userBusinessArea: this.props.userBusinessAreas[this.props.businessArea] });
    };

    showCreateSchemaModel = () => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaAction", "create"),
        );
    };

    showViewSchemaModel = (schemaId) => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaId", schemaId)
                .replace(":schemaAction", "view"),
        );
    };

    showEditSchemaModel = (schemaId) => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaId", schemaId)
                .replace(":schemaAction", "edit"),
        );
    };

    showSchemaModelHistory = (schemaId) => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaId", schemaId)
                .replace(":schemaAction", "history"),
        );
    };

    showExportSchemaModel = (schemaId) => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaId", schemaId)
                .replace(":schemaAction", "export"),
        );
    };

    showSchemaModelLineage = (schemaId) => {
        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schemaId", schemaId)
                .replace(":schemaAction", "lineage"),
        );
    };

    cancelManageSchemaModel = () => {
        if (this.state.parsedSchemaDefinition) {
            this.setState({
                parsedSchemaDefinition: null,
                manageParsedSchemaModelCancelled: true,
                scrollManageParsedSchemeDefinition: false,
            });
            return;
        }

        this.props.history.push(
            AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(":businessarea", this.props.businessArea.toLowerCase()),
        );
    };

    cancelExportSchemaModel = () => {
        this.props.history.goBack();
    };

    createSchemaModel = (schemaModel, afterSaveCallback) => {
        const normalizedSchemaModel = this.normalizePicklistFieldsForCreate(schemaModel);
        normalizedSchemaModel.active = true;
        this.setState({
            busy: true,
            busyMessage: "Creating schema model...",
            afterSaveCallback,
            submittedSchemaFieldNamesByIndex: this.getSchemaFieldNamesByIndex(normalizedSchemaModel.fields),
        });
        this.props.createSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, normalizedSchemaModel);
    };

    onAutomatedCreateSchemaModel = (schemaModel) => {
        const normalizedSchemaModel = this.normalizePicklistFieldsForCreate(schemaModel);
        normalizedSchemaModel.active = true;
        this.setState({
            busy: true,
            busyMessage: "Creating schema model...",
            goToEditSchema: true,
            submittedSchemaFieldNamesByIndex: this.getSchemaFieldNamesByIndex(normalizedSchemaModel.fields),
        });
        this.props.createSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, normalizedSchemaModel);
    };

    onUploadSchemaDefinitionFile = (dataFile) => {
        this.props.uploadSchemaModelDefinition(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, dataFile);
    };

    /** Changes picklist fields without a picklistType to have a default picklistType based on the presence of a dataDomainId */
    normalizePicklistFields = (fields) =>
        (fields || []).map((field) => {
            if (!field || field.dataType !== "picklist" || field.picklistType) {
                return field;
            }
            return {
                ...field,
                picklistType: field.dataDomainId ? "dynamic" : "static",
            };
        });

    /** Backstop so a picklist field can never reach the API without the picklistType the backend requires */
    normalizePicklistFieldsForCreate = (schemaModel) => {
        if (!schemaModel || !Array.isArray(schemaModel.fields)) {
            return schemaModel;
        }
        return { ...schemaModel, fields: this.normalizePicklistFields(schemaModel.fields) };
    };

    normalizePicklistFieldsForUpdate = (updateSchemaModelData) => {
        const normalizedUpdateData = _.cloneDeep(updateSchemaModelData);
        normalizedUpdateData.newFields = this.normalizePicklistFields(normalizedUpdateData.newFields);
        normalizedUpdateData.updatedFields = this.normalizePicklistFields(normalizedUpdateData.updatedFields);
        return normalizedUpdateData;
    };

    updateSchemaModel = (schemaId, updateSchemaModelData, fieldIndexMappings, afterSaveCallback) => {
        const normalizedUpdateSchemaModelData = this.normalizePicklistFieldsForUpdate(updateSchemaModelData);
        this.setState({
            busy: true,
            busyMessage: "Updating schema model...",
            afterSaveCallback,
            submittedSchemaFieldNamesByIndex: fieldIndexMappings.fieldNamesByIndex || {},
        });
        this.props.updateSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, schemaId, normalizedUpdateSchemaModelData, fieldIndexMappings);
    };

    onAutoTagModalClose = () => {
        this.setState({
            showAutoTagModal: false,
            autoTagSuggestions: null,
        });
    };

    onApplyAutoTagSuggestions = (selectedSuggestions) => {
        if (this.manageSchemaModelRef && this.manageSchemaModelRef.current) {
            this.manageSchemaModelRef.current.onApplyAutoTagSuggestions(selectedSuggestions);
        } else {
            message.error("Unable to apply auto tag suggestions at this moment. Please try again later.");
        }
        this.onAutoTagModalClose();
    };

    showDeleteSchemaModelConfirmation = (schemaModel) => {
        this.setState({
            deletedSchemaModel: schemaModel,
            showSchemaModelDeleteConfirmation: true,
            deleteSchemaModelConfirmMessage: `Are you sure, you want to delete '${schemaModel.name}' schema model?`,
        });
    };

    onDeleteSchemaModelConfirmed = () => {
        this.setState({
            busy: true,
            busyMessage: "Deleting schema model...",
        });
        this.props.deleteSchemaModel(this.state.deletedSchemaModel.businessAreaId, this.state.deletedSchemaModel.schemaId);
        this.onDeleteSchemaModelCancel();
    };

    onDeleteSchemaModelCancel = () => {
        this.setState({
            deletedSchemaModel: null,
            showSchemaModelDeleteConfirmation: false,
            deleteSchemaModelConfirmMessage: "",
        });
    };

    onSchemaModelValuesChanged = (changedValues, allValues) => {
        if (_.isEmpty(this.state.manageSchemaModelErrors) === false) {
            let manageSchemaModelErrors = { ...this.state.manageSchemaModelErrors };
            let flatObject = flatten(changedValues);
            for (let key in flatObject) {
                delete manageSchemaModelErrors[key];
            }
            this.setState({
                manageSchemaModelErrors,
            });
        }
    };

    onSchemaModelsPageSelect = (pagination) => {
        if (this.props.schemaModelListResult.pageKey && pagination.current * pagination.pageSize >= this.props.schemaModelListResult.Items.length) {
            this.props.getSchemaModelsNextPageRequest(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, this.props.schemaModelListResult.pageKey);
        }
    };

    isBusy = () => {
        let isBusy = this.state.busy || this.state.fetchingUserBusinessArea || this.state.fetchingPermission;
        if (this.props.action && (this.props.action.toLowerCase() === "view" || this.props.action.toLowerCase() === "edit")) {
            isBusy = isBusy || this.state.fetchingSchemaModel;
        }
        return isBusy;
    };

    getSelectedSchemaModelFromHash = () => {
        if (this.props.history.location.hash) {
            let hashValue = decodeURIComponent(this.props.history.location.hash);
            if (this.props.schemaModelListResult.Items && this.props.schemaModelListResult.Items.length > 0) {
                let selectedSchemaName = hashValue.slice(1);
                if (selectedSchemaName) {
                    let selectedSchemaModel = this.props.schemaModelListResult.Items.find((schemaModel) => schemaModel.name.toLowerCase() === selectedSchemaName.toLowerCase());
                    if (selectedSchemaModel) {
                        return selectedSchemaModel;
                    }
                }
            }
        }
        return null;
    };

    manuallyCreateSchema = () => {
        this.setState({
            createSchemaManually: true,
        });
    };

    onManageParsedSchemaModel = (schemaName, schemaFields) => {
        if (schemaFields) {
            this.setState(
                {
                    parsedSchemaDefinition: {
                        name: schemaName,
                        description: schemaName,
                        fields: schemaFields,
                    },
                    manageParsedSchemaModelCancelled: false,
                },
                () => {
                    this.setState({
                        scrollManageParsedSchemeDefinition: true,
                    });
                },
            );
        }
    };

    onCancelManageParsedSchemaModel = () => {
        this.setState({
            parsedSchemaDefinition: null,
            manageParsedSchemaModelCancelled: true,
            scrollManageParsedSchemeDefinition: false,
        });
    };

    /**
     * Suggestions arrived (or sampling was declined). Show the review step.
     * PRE-2722: nothing is applied here; the user decides first.
     */
    onSchemaProfileReady = (schemaName) => {
        this.setState({ reviewingSuggestionsFor: schemaName });
    };

    onCancelSuggestionReview = () => {
        this.setState({ reviewingSuggestionsFor: null });
        this.props.resetSchemaProfile();
    };

    /**
     * Apply only the accepted suggestions and hand off to the normal schema
     * form, where everything remains editable.
     *
     * With nothing accepted this yields the proposal baselines, which are the
     * same plain fields the unassisted flow produces (AC5).
     */
    onApplyAcceptedSuggestions = () => {
        const { schemaProfile, suggestionStatuses } = this.props;
        const schemaName = this.state.reviewingSuggestionsFor;

        if (!schemaProfile) {
            // Sampling was unavailable, so there is nothing to apply. Fall back
            // to the plain flow rather than blocking the user.
            this.setState({ reviewingSuggestionsFor: null });
            this.props.resetSchemaProfile();
            return;
        }

        const { fields, acceptedCount } = applyAcceptedSuggestions(schemaProfile, suggestionStatuses);
        const picklistCandidates = collectAcceptedPicklistCandidates(schemaProfile, suggestionStatuses);

        if (picklistCandidates.length > 0) {
            // PRE-2720: generation, AI-paired descriptions and the
            // static/dynamic choice belong to the PRE-2699 accelerator. Until
            // PRE-2706..2709 land there is nothing to hand off to, so say so
            // rather than silently dropping the user's accepted candidates.
            showInfo(
                `${picklistCandidates.length} picklist candidate(s) noted. Picklist values are configured in the schema form.`,
            );
        }

        this.setState({ reviewingSuggestionsFor: null }, () => {
            this.props.resetSchemaProfile();
            this.onManageParsedSchemaModel(schemaName, fields);
            if (acceptedCount > 0) {
                showInfo(`Applied ${acceptedCount} suggestion(s). Review and adjust before saving.`);
            }
        });
    };

    showCreateFromSystemConnection = () => {
        this.setState({
            createFromSystemConnection: true,
            createSchemaManually: false,
            selectedSystemConnection: null,
            fetchingSystemConnections: true,
        });
        this.props.getSystemConnections(this.props.userBusinessAreas[this.props.businessArea].businessAreaId);
    };

    getKeyByTypeExists = () => {
        this.props.getKeyByTypeExists("OPEN_AI_KEY");
    };

    bulkCreateDataTag = (dataTag) => {
        this.props.bulkCreateDataTag(dataTag.tags);
    };

    onSystemConnectionSelected = (connection, stepResult) => {
        const isSalesforce = connection.connectionType === "salesforce";
        const isHubspot = connection.connectionType === "hubspot";
        const isSnowflake = connection.connectionType === "snowflake";
        const isJdbc = connection.connectionType === "jdbc";

        // Salesforce and HubSpot already describe the object in the selector
        // (fields arrive on fileReadResult.meta). Hand them to the wizard as
        // { Name, fields } so it renders immediately; a bare table-name string
        // makes the wizard wait for a JDBC/Snowflake schema fetch that never
        // fires for these connectors, leaving the Review Fields spinner stuck.
        let selectedTable = null;
        if (isSalesforce) {
            selectedTable = { Name: connection.file?.tableName, fields: connection.fileReadResult?.meta?.salesforceFields };
        } else if (isHubspot) {
            selectedTable = { Name: connection.file?.tableName, fields: connection.fileReadResult?.meta?.hubspotFields };
        } else if (connection.file && connection.file.tableName) {
            selectedTable = connection.file.tableName;
        }

        this.setState(
            {
                selectedSystemConnection: connection,
                selectedTable,
            },
            () => {
                if (connection.file && connection.file.tableName) {
                    if (isJdbc) {
                        this.props.getJdbcTableSchema(connection.connectionId, connection.file.tableName);
                    } else if (isSnowflake) {
                        this.props.getSnowflakeTableSchema(connection.connectionId, connection.file.tableName);
                    }
                }
            },
        );
    };

    handleCreateSchemaFromSystemConnection = (schemaData) => {
        const { name, description, fields, table, connection } = schemaData;
        const noMinMaxTypes = ["date", "datetime", "time", "date-year", "date-month", "date-day", "boolean", "picklist", "latlng", "attachment", "image", "document", "video"];
        const schemaModel = {
            name,
            description,
            fields: fields.map((f) => {
                const dataType = f.dataType || "string";
                const field = {
                    isPrimaryKey: f.isPrimaryKey || false,
                    isSortKey: f.isSortKey || false,
                    active: f.active || true,
                    dataType,
                    isRequired: f.isRequired || false,
                    isMultivalue: f.isMultivalue || false,
                    isPII: f.isPII || false,
                    name: f.name,
                    description: f.description || f.name,
                };
                // Only include min/max for types that accept numeric ranges
                if (!noMinMaxTypes.includes(dataType)) {
                    field.min = f.min || 1;
                    field.max = f.max || 500;
                }
                // Carry constraints enriched by accepted auto-configure
                // suggestions; without this a DATE_FORMAT / boolean-style
                // suggestion is dropped from the create payload (PRE-2716).
                if (f.dateFormat) field.dateFormat = f.dateFormat;
                if (f.dateSeparator) field.dateSeparator = f.dateSeparator;
                if (f.timeFormat) field.timeFormat = f.timeFormat;
                if (f.allowedBooleanValues) field.allowedBooleanValues = f.allowedBooleanValues;
                return field;
            }),
            source: {
                type: connection.connectionType,
                connectionId: connection.connectionId,
                tableName: table && typeof table === "object" ? table.Name : table,
            },
            active: true,
        };
        this.setState({ busy: true, busyMessage: "Creating schema model..." });
        this.props.createSchemaModel(this.props.userBusinessAreas[this.props.businessArea].businessAreaId, schemaModel);
    };

    getSchemaComponent = () => {
        if (!this.props.action) {
            return (
                <>
                    <OkCancelConfirmationBox
                        show={this.state.showSchemaModelDeleteConfirmation}
                        message={this.state.deleteSchemaModelConfirmMessage}
                        onCancel={this.onDeleteSchemaModelCancel}
                        onOk={this.onDeleteSchemaModelConfirmed}
                    ></OkCancelConfirmationBox>
                    {!this.state.fetchingSchemaModels ? (
                        <SchemaModelList
                            autoScrollTo
                            schemaModelList={this.props.schemaModelListResult.Items}
                            onCreateNewSchemaModel={this.showCreateSchemaModel}
                            onDeleteSchemaModel={this.showDeleteSchemaModelConfirmation}
                            onViewSchemaModel={this.showViewSchemaModel}
                            onEditSchemaModel={this.showEditSchemaModel}
                            permission={this.state.permission}
                            onPageSelect={this.onSchemaModelsPageSelect}
                            onViewSchemaModelHistory={this.showSchemaModelHistory}
                            onViewSchemaModelLineage={this.showSchemaModelLineage}
                            onSchemaModelSelected={this.onSchemaModelSelected}
                            selectedSchemaModel={this.getSelectedSchemaModelFromHash()}
                        ></SchemaModelList>
                    ) : (
                        <></>
                    )}
                </>
            );
        }
        switch (this.props.action.toLowerCase()) {
            case "create":
                if (this.state.createFromSystemConnection) {
                    if (!this.state.selectedSystemConnection) {
                        return (
                            <SystemConnectionSelector
                                businessAreaId={this.props.userBusinessAreas[this.props.businessArea]?.businessAreaId}
                                onConnectionSelectionComplete={this.onSystemConnectionSelected}
                                importButtonLabel="Import"
                            />
                        );
                    }
                    return (
                        <SchemaModelCreateFromSystemConnection
                            systemConnection={this.state.selectedSystemConnection}
                            jdbcConnectionTables={this.props.jdbcConnectionTables}
                            getJdbcConnectionTablesResult={this.props.getJdbcConnectionTablesResult}
                            getJdbcTableSchemaResult={this.props.getJdbcTableSchemaResult}
                            jdbcTableSchema={this.props.jdbcTableSchema}
                            snowflakeConnectionTables={this.props.snowflakeConnectionTables}
                            getSnowflakeConnectionTablesResult={this.props.getSnowflakeConnectionTablesResult}
                            getSnowflakeTableSchemaResult={this.props.getSnowflakeTableSchemaResult}
                            snowflakeTableSchema={this.props.snowflakeTableSchema}
                            onCreateSchema={this.handleCreateSchemaFromSystemConnection}
                            onGetJdbcConnectionTables={this.props.getJdbcConnectionTables}
                            onGetJdbcTableSchema={this.props.getJdbcTableSchema}
                            onGetSnowflakeConnectionTables={this.props.getSnowflakeConnectionTables}
                            onGetSnowflakeTableSchema={this.props.getSnowflakeTableSchema}
                            initialSelectedTable={this.state.selectedTable}
                            onSampleConnection={this.props.sampleConnection}
                            schemaProfile={this.props.schemaProfile}
                            schemaProfileUnsupported={this.props.schemaProfileUnsupported}
                            getSchemaProfileResult={this.props.getSchemaProfileResult}
                            suggestionStatuses={this.props.suggestionStatuses}
                            onSetSuggestionStatus={this.props.setSuggestionStatus}
                            onSetAllSuggestionStatuses={this.props.setAllSuggestionStatuses}
                            onResetSchemaProfile={this.props.resetSchemaProfile}
                        />
                    );
                }
                return this.state.createSchemaManually ? (
                    <ManageSchemaModel
                        action={this.props.action}
                        formErrors={this.state.manageSchemaModelErrors}
                        onValuesChanged={this.onSchemaModelValuesChanged}
                        onCancel={this.cancelManageSchemaModel}
                        createSchemaModel={this.createSchemaModel}
                        permission={this.state.permission}
                        keyExists={this.props.keyExists}
                        dataTagsByBusinessAreaIdResult={this.props.dataTagsByBusinessAreaIdResult}
                        ref={this.manageSchemaModelRef}
                        autoTagging={this.props.autoTaggingSchema}
                        autoTaggingData={this.props.autoTaggingData}
                        autoTaggingResult={this.props.autoTaggingResult}
                        getBusinessAreaDataForBusinessAreaResult={this.props.getBusinessAreaDataForBusinessAreaResult}
                        businessAreaDataList={this.props.businessAreaDataList}
                    ></ManageSchemaModel>
                ) : (
                    <FlexContainerLayout>
                        {this.state.reviewingSuggestionsFor ? (
                            <FlexContainerLayout>
                                <SchemaSuggestionReview
                                    schemaProfile={this.props.schemaProfile}
                                    suggestionStatuses={this.props.suggestionStatuses}
                                    counts={selectSuggestionCounts(this.props.schemaProfile, this.props.suggestionStatuses)}
                                    unsupported={this.props.schemaProfileUnsupported}
                                    statusFilter={this.state.suggestionStatusFilter || "ALL"}
                                    onStatusFilterChange={(suggestionStatusFilter) =>
                                        this.setState({ suggestionStatusFilter })
                                    }
                                    onSetSuggestionStatus={this.props.setSuggestionStatus}
                                    onSetAllSuggestionStatuses={this.props.setAllSuggestionStatuses}
                                    onContinue={this.onApplyAcceptedSuggestions}
                                    onCancel={this.onCancelSuggestionReview}
                                ></SchemaSuggestionReview>
                            </FlexContainerLayout>
                        ) : !this.state.parsedSchemaDefinition ? (
                            <FlexContainerLayout>
                                <SchemaModelCreateAutomation
                                    tenant={this.props.tenant}
                                    businessArea={this.props.businessArea}
                                    onManuallyCreateSchema={this.manuallyCreateSchema}
                                    onCreateSchemaModel={this.onAutomatedCreateSchemaModel}
                                    onUploadSchemaDefinitionFile={this.onUploadSchemaDefinitionFile}
                                    uploadSchemaModelDefinitionResult={this.props.uploadSchemaModelDefinitionResult}
                                    schemaModelDefinitionFile={this.props.schemaModelDefinitionFile}
                                    getParsedSchemaDefinition={this.props.getParsedSchemaDefinition}
                                    getParsedSchemaDefinitionResult={this.props.getParsedSchemaDefinitionResult}
                                    schemaDefinition={this.props.schemaDefinition}
                                    onManageParsedSchemaModel={this.onManageParsedSchemaModel}
                                    onCancelManageParsedSchemaModel={this.onCancelManageParsedSchemaModel}
                                    manageParsedSchemaModelCancelled={this.state.manageParsedSchemaModelCancelled}
                                    onCreateFromSystemConnection={this.showCreateFromSystemConnection}
                                    getSchemaProfile={this.props.getSchemaProfile}
                                    getSchemaProfileResult={this.props.getSchemaProfileResult}
                                    schemaProfile={this.props.schemaProfile}
                                    schemaProfileUnsupported={this.props.schemaProfileUnsupported}
                                    onSchemaProfileReady={this.onSchemaProfileReady}
                                ></SchemaModelCreateAutomation>
                            </FlexContainerLayout>
                        ) : (
                            <FlexContainerLayout scrollContentToBottom={this.state.scrollManageParsedSchemeDefinition}>
                                <ManageSchemaModel
                                    defaultValue={this.state.parsedSchemaDefinition}
                                    action={this.props.action}
                                    formErrors={this.state.manageSchemaModelErrors}
                                    onValuesChanged={this.onSchemaModelValuesChanged}
                                    onCancel={this.cancelManageSchemaModel}
                                    createSchemaModel={this.createSchemaModel}
                                    permission={this.state.permission}
                                    getBusinessAreaDataForBusinessAreaResult={this.props.getBusinessAreaDataForBusinessAreaResult}
                                    businessAreaDataList={this.props.businessAreaDataList}
                                    dataTagsByBusinessAreaIdResult={this.props.dataTagsByBusinessAreaIdResult}
                                    keyExists={this.props.keyExists}
                                    ref={this.manageSchemaModelRef}
                                    autoTagging={this.props.autoTaggingSchema}
                                    autoTaggingData={this.props.autoTaggingData}
                                    autoTaggingResult={this.props.autoTaggingResult}
                                ></ManageSchemaModel>
                            </FlexContainerLayout>
                        )}
                    </FlexContainerLayout>
                );
            case "view":
            case "edit":
                return !this.state.fetchingSchemaModel ? (
                    this.props.schemaModel ? (
                        <ManageSchemaModel
                            action={this.props.action}
                            schemaModel={this.props.schemaModel}
                            onValuesChanged={this.onSchemaModelValuesChanged}
                            onCancel={this.cancelManageSchemaModel}
                            formErrors={this.state.manageSchemaModelErrors}
                            updateSchemaModel={this.updateSchemaModel}
                            permission={this.state.permission}
                            getBusinessAreaDataForBusinessAreaResult={this.props.getBusinessAreaDataForBusinessAreaResult}
                            businessAreaDataList={this.props.businessAreaDataList}
                            dataTagsByBusinessAreaIdResult={this.props.dataTagsByBusinessAreaIdResult}
                            keyExists={this.props.keyExists}
                            ref={this.manageSchemaModelRef}
                            autoTagging={this.props.autoTaggingSchema}
                            autoTaggingData={this.props.autoTaggingData}
                            autoTaggingResult={this.props.autoTaggingResult}
                            exportSchema={this.showExportSchemaModel}
                            onSwitchEditMode={this.showEditSchemaModel}
                        ></ManageSchemaModel>
                    ) : this.props.getSchemaModelResult.success ? (
                        <MessageBox negative floating size="big" header="Schema Model not found.">
                            <p>The Schema Model you are looking for does not exist.</p>
                        </MessageBox>
                    ) : (
                        <></>
                    )
                ) : (
                    <></>
                );
            case "history":
                return this.state.showHistory ? <SchemaModelAudit schemaModel={this.props.schemaModel}></SchemaModelAudit> : <></>;
            case "export":
                return this.state.showExport ? <SchemaExport schemaModel={this.props.schemaModel} cancelExportSchema={this.cancelExportSchemaModel}></SchemaExport> : <></>;
            case "lineage":
                return this.state.showLineage ? (
                    <SchemaModelLineage schemaModel={this.props.schemaModel} businessArea={this.props.businessArea} tenant={this.props.tenant}></SchemaModelLineage>
                ) : (
                    <></>
                );
            default:
                return <ProhibitedArea></ProhibitedArea>;
        }
    };

    // Loads the business area again after it failed to load.
    retryBusinessArea = () => {
        this.setState({ businessAreaFailure: null, fetchingUserBusinessArea: true });
        this.props.getUserBusinessAreaByName(this.props.businessArea);
    };

    render() {
        if (this.state.permissionDenied) {
            return <ProhibitedArea></ProhibitedArea>;
        }
        if (this.state.businessAreaFailure) {
            return (
                <BusinessAreaLoadError
                    kind={this.state.businessAreaFailure}
                    name={this.props.businessArea}
                    listPath={AppPaths.TENANT_HOME.replace(":tenant", this.props.tenant.toLowerCase())}
                    onRetry={this.retryBusinessArea}
                />
            );
        }
        return (
            <div className="relative flex w-full flex-1 flex-col overflow-auto">
                <AutoTaggingModal
                    visible={this.state.showAutoTagModal}
                    suggestions={this.state.autoTagSuggestions}
                    onCancel={this.onAutoTagModalClose}
                    onApply={this.onApplyAutoTagSuggestions}
                    existingTags={this.props.dataTagsByBusinessAreaIdResult?.Items || []}
                    businessAreaId={this.props.userBusinessAreas[this.props.businessArea]?.businessAreaId}
                    businessAreaName={this.props.userBusinessAreas[this.props.businessArea]?.name}
                    onBulkCreateTags={this.bulkCreateDataTag}
                    bulkCreateResult={this.props.bulkCreateDataTagResult}
                    bulkCreateTagsResult={this.props.bulkCreateTagsResult}
                />
                <LoadingOverlay busy={this.isBusy()} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                {!this.state.fetchingUserBusinessArea && this.getSchemaComponent()}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        userBusinessAreas: state.schemaModels.userBusinessAreas,
        getUserBusinessAreaByNameResult: state.schemaModels.getUserBusinessAreaByNameResult,
        schemaModelListResult: state.schemaModels.schemaModelListResult,
        recentSchemaModelListResult: state.schemaModels.recentSchemaModelListResult,
        getSchemaModelsResult: state.schemaModels.getSchemaModelsResult,
        createSchemaModelResult: state.schemaModels.createSchemaModelResult,
        createdSchemaModel: state.schemaModels.createdSchemaModel,
        deleteSchemaModelResult: state.schemaModels.deleteSchemaModelResult,
        getSchemaModelResult: state.schemaModels.getSchemaModelResult,
        schemaModel: state.schemaModels.schemaModel,
        updateSchemaModelResult: state.schemaModels.updateSchemaModelResult,
        getUserSchemaModelsPermissionResult: state.schemaModels.getUserSchemaModelsPermissionResult,
        permission: state.schemaModels.permission,
        getBusinessAreaDataForBusinessAreaResult: state.schemaModels.getBusinessAreaDataForBusinessAreaResult,
        businessAreaDataList: state.schemaModels.businessAreaDataList,
        uploadSchemaModelDefinitionResult: state.schemaModels.uploadSchemaModelDefinitionResult,
        schemaModelDefinitionFile: state.schemaModels.schemaModelDefinitionFile,
        getParsedSchemaDefinitionResult: state.dataParser.getParsedSchemaDefinitionResult,
        schemaDefinition: state.dataParser.schemaDefinition,
        getSchemaProfileResult: state.schemaProfile.getSchemaProfileResult,
        schemaProfile: state.schemaProfile.schemaProfile,
        suggestionStatuses: state.schemaProfile.suggestionStatuses,
        schemaProfileUnsupported: state.schemaProfile.unsupported,
        systemConnectionListResult: state.systemConnection.systemConnectionListResult,
        getSystemConnectionsResult: state.systemConnection.getSystemConnectionsResult,
        jdbcConnectionTables: state.systemConnectionJdbc.jdbcConnectionTables,
        getJdbcConnectionTablesResult: state.systemConnectionJdbc.getJdbcConnectionTablesResult,
        jdbcTablePreview: state.systemConnectionJdbc.jdbcTablePreview,
        getJdbcTablePreviewResult: state.systemConnectionJdbc.getJdbcTablePreviewResult,
        jdbcTableSchema: state.systemConnectionJdbc.jdbcTableSchema,
        getJdbcTableSchemaResult: state.systemConnectionJdbc.getJdbcTableSchemaResult,
        snowflakeConnectionTables: state.systemConnectionSnowflake.snowflakeConnectionTables,
        getSnowflakeConnectionTablesResult: state.systemConnectionSnowflake.getSnowflakeConnectionTablesResult,
        snowflakeTableSchema: state.systemConnectionSnowflake.snowflakeTableSchema,
        getSnowflakeTableSchemaResult: state.systemConnectionSnowflake.getSnowflakeTableSchemaResult,
        checkedKeys: state.systemKeys.checkedKeys,
        dataTagsByBusinessAreaIdResult: state.dataTags.dataTagsByBusinessAreaIdResult,
        keyExists: state.systemKeys.keyExists,
        getKeyByTypeExistsResult: state.systemKeys.getKeyByTypeExistsResult,
        autoTaggingResult: state.dataTags.autoTaggingResult,
        autoTaggingData: state.dataTags.autoTaggingData,
        bulkCreateDataTagResult: state.dataTags.bulkCreateDataTagResult,
        bulkCreateTagsResult: state.dataTags.bulkCreateTagsResult,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getUserBusinessAreaByName: (businessAreaName) => dispatch(actions.getUserBusinessAreaByNameRequest(businessAreaName)),
        getSchemaModels: (businessAreaId) => dispatch(actions.getSchemaModelsRequest(businessAreaId)),
        getSchemaModelsNextPageRequest: (businessAreaId, pageKey) => dispatch(actions.getSchemaModelsNextPageRequest(businessAreaId, pageKey)),
        createSchemaModel: (businessAreaId, schemaModel) => dispatch(actions.createSchemModelRequest(businessAreaId, schemaModel)),
        deleteSchemaModel: (businessAreaId, schemaId) => dispatch(actions.deleteSchemaModelRequest(businessAreaId, schemaId)),
        getSchemaModel: (businessAreaId, schemaId) => dispatch(actions.getSchemaModelRequest(businessAreaId, schemaId)),
        updateSchemaModel: (businessAreaId, schemaId, updateSchemaModelData, fieldIndexMappings) =>
            dispatch(actions.updateSchemaModelRequest(businessAreaId, schemaId, updateSchemaModelData, fieldIndexMappings)),
        getUserSchemasPermission: () => dispatch(actions.getUserSchemaModelsPermissionRequest()),
        getBusinessAreaDataForBusinessArea: (businessAreaId) => dispatch(actions.getBusinessAreaDataForBusinessAreaRequest(businessAreaId)),
        uploadSchemaModelDefinition: (businessAreaId, dataFile) => dispatch(actions.uploadSchemaModelDefinitionRequest(businessAreaId, dataFile)),
        getParsedSchemaDefinition: (parseConfig) => dispatch(actions.getParsedSchemaDefinitionRequest(parseConfig)),
        getSchemaProfile: (profileConfig) => dispatch(actions.getSchemaProfileRequest(profileConfig)),
        sampleConnection: (params) => dispatch(actions.sampleConnectionRequest(params)),
        setSuggestionStatus: (suggestionId, status) => dispatch(actions.setSuggestionStatus(suggestionId, status)),
        setAllSuggestionStatuses: (status, fieldName) => dispatch(actions.setAllSuggestionStatuses(status, fieldName)),
        resetSchemaProfile: () => dispatch(actions.resetSchemaProfile()),
        getSystemConnections: () => dispatch(actions.getSystemConnectionsRequest()),
        getJdbcConnectionTables: (connectionId) => dispatch(actions.getJdbcConnectionTablesRequest(connectionId)),
        getJdbcTableSchema: (connectionId, tableName) => dispatch(actions.getJdbcTableSchemaRequest(connectionId, tableName)),
        getSnowflakeConnectionTables: (connectionId) => dispatch(actions.getSnowflakeConnectionTablesRequest(connectionId)),
        getSnowflakeTableSchema: (connectionId, tableName) => dispatch(actions.getSnowflakeTableSchemaRequest(connectionId, tableName)),
        getKeyByTypeExists: (keyType) => dispatch(actions.getKeyByTypeExistsRequest(keyType)),
        autoTaggingSchema: (dataTags) => dispatch(actions.autoTaggingRequest(dataTags)),
        bulkCreateDataTag: (dataTag) => dispatch(actions.bulkCreateDataTagRequest(dataTag)),
        addBulkCreatedTagsToBusinessArea: (businessAreaId, dataTags) => dispatch(actions.addBulkCreatedTagsToBusinessArea(businessAreaId, dataTags)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SchemaModels));
